/**
 * Loads Recurly.js once and hands back the configured `recurly` global (BIO-1869), ported from
 * app's `services/recurly.ts`.
 *
 * Recurly requires the library to come from its CDN, never bundled, so this injects the script
 * tag on first use instead of importing a package. The promise is memoised: every caller shares one
 * load, and a failed load is forgotten so the next call can retry. This replaces the
 * `setInterval(loadStripe, 500)` poll the Stripe form ran.
 */
export const RECURLY_SCRIPT_URL = 'https://js.recurly.com/v4/recurly.js';

declare global {
  interface Window {
    recurly?: any;
  }
}

let loading: Promise<any> | null = null;

const injectScript = (): Promise<void> =>
  new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = RECURLY_SCRIPT_URL;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Recurly.js failed to load'));
    document.head.appendChild(script);
  });

const load = async (): Promise<any> => {
  const publicKey = process.env.RECURLY_PUBLIC;
  if (!publicKey) {
    throw new Error('RECURLY_PUBLIC is not set; payments cannot be taken here');
  }
  if (!window.recurly) {
    await injectScript();
  }
  const { recurly } = window;
  if (!recurly) {
    throw new Error('Recurly.js loaded but did not define window.recurly');
  }
  recurly.configure({ publicKey });
  await new Promise<void>((resolve) => {
    recurly.ready(() => resolve());
  });

  return recurly;
};

export const getRecurly = (): Promise<any> => {
  if (!loading) {
    loading = load().catch((e) => {
      loading = null;
      throw e;
    });
  }

  return loading;
};

/** Test hook: forget the memoised load. */
export const resetRecurlyForTests = () => {
  loading = null;
};

/** recurly.token as a promise. `data` is a plain object of Recurly.js field names. */
export const tokenizeCard = (
  recurly: any,
  elements: any,
  data: Record<string, string>
): Promise<{ id: string; type?: string }> =>
  new Promise((resolve, reject) => {
    recurly.token(elements, data, (err: any, token: any) =>
      err ? reject(err) : resolve(token)
    );
  });
